/**
 * Custom hook that provides a bound restoreEvolu function.
 *
 * Encapsulates the Evolu instance access and restoreWithMnemonic call
 * so that components don't need to call useEvolu() directly.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module auth/hooks/useRestoreEvolu
 */

import { useMemo } from 'react';
import { useEvolu } from '@evolu/react';
import type { Evolu } from '@evolu/common';
import { restoreWithMnemonic } from '@/db/database';
import type { SatsflowSchema } from '@/db/database';

/** Return type for the useRestoreEvolu hook. */
interface UseRestoreEvoluResult {
  /** Restore Evolu database from a BIP-85 child mnemonic. */
  readonly restoreEvolu: (childMnemonic: string) => Promise<void>;
}

/**
 * Hook that provides a bound function to restore Evolu with a child mnemonic.
 *
 * Injects the current Evolu instance into the restoreWithMnemonic call,
 * so consumers don't need direct access to the Evolu instance.
 *
 * @returns Object containing the bound restoreEvolu function
 */
export function useRestoreEvolu(): UseRestoreEvoluResult {
  const evolu = useEvolu();

  const restoreEvolu = useMemo(
    () =>
      (childMnemonic: string): Promise<void> =>
        restoreWithMnemonic(evolu as unknown as Evolu<SatsflowSchema>, childMnemonic),
    [evolu],
  );

  return { restoreEvolu };
}
